import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Container, 
  Box, 
  Typography, 
  CircularProgress, 
  Paper,
  Stepper, 
  Step, 
  StepLabel,
  Alert 
} from '@mui/material';
import {
  handleMyLoginCallback,
  initiateMyLoginAuth,
  validateMyLoginAuthState,
} from '../../../utils/myLoginAuth';
import { useDispatch } from 'react-redux';
import { useAuth } from '../../../context/AuthContext';
import paths from '../../../routes/paths';
import MyLoginErrorHandler from '../../../components/auth/MyLoginErrorHandler';
import { loginSuccess, loginFailure } from '../../../actions/authActions';
import { styled } from '@mui/material/styles';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { verifyMyLoginUser } from '../../../services/api/myLogin/myLoginApi';
import WbLoader from '../../../components/common/WbLoader';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
  width: '100%',
  maxWidth: 600,
}));

const SuccessIcon = styled(CheckCircleOutlineIcon)(({ theme }) => ({
  fontSize: 60,
  color: theme.palette.success.main,
  marginBottom: theme.spacing(2),
}));

/**
 * Enhanced MyLogin callback component that handles the OAuth2 authentication process
 * Includes step visualization, user verification, and graceful error handling
 */
const MyLoginCallback = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { dispatch: authDispatch, actions } = useAuth();
  
  // Authentication steps
  const steps = [
    'Verifying login',
    'Exchanging credentials',
    'Loading your profile',
    'Setting up your session'
  ];

  // Handle retry logic
  const handleRetry = useCallback(() => {
    // Reset state
    setLoading(true);
    setError(null);
    setActiveStep(0);
    
    // Retry authentication from scratch
    initiateMyLoginAuth(paths.pupilSurveys);
  }, []);

  useEffect(() => {
    const processCallback = async () => {
      try {
        // Step 1: Get the authorization code and state from URL
        const params = new URLSearchParams(location.search);
        const code = params.get('code');
        const state = params.get('state');
        const error = params.get('error');
        const errorDescription = params.get('error_description');

        // Handle error in the OAuth callback
        if (error) {
          throw new Error(errorDescription || `Authentication error: ${error}`);
        }

        if (!code) {
          throw new Error('No authorization code received from MyLogin');
        }

        if (!state) {
          throw new Error('No state parameter received from MyLogin');
        }

        // Fail closed if callback state does not match the session-bound state.
        if (!validateMyLoginAuthState(state)) {
          throw new Error('State validation failed. Please try signing in again.');
        }

        setActiveStep(1);
        
        // Step 2: Handle the callback to exchange code for tokens
        const authData = await handleMyLoginCallback(code, state, {
          skipStateValidation: true,
        });
        if (!authData || !authData.tokens) {
          throw new Error('Failed to obtain authentication tokens');
        }
        
        setActiveStep(2);
        
        // Step 3: Verify the MyLogin user with our backend
        try {

            const verificationResult = await verifyMyLoginUser(authData);
          
          if (!verificationResult || !verificationResult.success) {
            throw new Error('Failed to verify user with our system');
          }
          
          // Use the user data from verification result
          const userData = {
            ...verificationResult.user,
            idToken: authData.userInfo?.data?.id || '',
            email: authData.userInfo?.data?.email || '',
            provider: 'mylogin'
          };

          // Always store the pupil object in localStorage as a string
          try {
            localStorage.setItem('pupil', JSON.stringify(userData));
          } catch (e) {
            // Fallback: clear localStorage if it fails
            localStorage.removeItem('pupil');
          }

          setActiveStep(3);
          // Step 4: Store the authentication data in Redux store and Context
          dispatch(loginSuccess({
            user: userData,
            provider: 'mylogin'
          }));
          // Also update the auth context
          authDispatch({ 
            type: actions.LOGIN_SUCCESS, 
            payload: { 
              user: userData, 
              provider: 'mylogin' 
            } 
          });
          
          // Successfully completed authentication
          setSuccess(true);
          
          // Check for redirect URLs in multiple possible locations
          const myLoginRedirect = localStorage.getItem('mylogin_redirect_after_login');
          const authRedirect = localStorage.getItem('auth_redirect_url');
          const legacyRedirect = localStorage.getItem('RedirectUrl');
          
          // Use the first available redirect URL or default to surveys
          const redirectUrl = myLoginRedirect || authRedirect || legacyRedirect || paths.pupilSurveys;
          
          // Clear all stored redirect URLs
          localStorage.removeItem('mylogin_redirect_after_login');
          localStorage.removeItem('auth_redirect_url');
          localStorage.removeItem('RedirectUrl');
          
          // Redirect to the saved URL or dashboard after a short delay to show success message
          setTimeout(() => {
            navigate(redirectUrl, { replace: true });
          }, 1500);
          
        } catch (verifyError) {
          console.error('Failed to verify user with backend:', verifyError);
          setError(verifyError.message || 'There was a problem verifying your MyLogin account.');
          dispatch(loginFailure(verifyError.message));
          
          // Also update the auth context
          authDispatch({ 
            type: actions.LOGIN_FAILURE, 
            payload: verifyError.message 
          });
        }
      } catch (err) {
        console.error('Failed to process MyLogin callback:', err);
        setError(err.message || 'There was a problem signing you in with MyLogin.');
        dispatch(loginFailure(err.message));
        
        // Also update the auth context
        authDispatch({ 
          type: actions.LOGIN_FAILURE, 
          payload: err.message 
        });
      } finally {
        setLoading(false);
      }
    };

    processCallback();
  }, [location, navigate, dispatch, authDispatch, actions]);

  // If we're still checking the initial auth state
  if (loading && activeStep === 0) {
    return <WbLoader />;
  }

  // If there was an error during authentication
  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 5 }}>
        <MyLoginErrorHandler 
          error={error}
          onRetry={handleRetry}
        />
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 5, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
      <StyledPaper elevation={3}>
        {success ? (
          <Box sx={{ textAlign: 'center', p: 2 }}>
            <SuccessIcon />
            <Typography variant="h5" gutterBottom>
              Authentication Successful
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              You have been successfully logged in with MyLogin.
            </Typography>
            <Typography variant="body2" color="primary">
              Redirecting you to the dashboard...
            </Typography>
          </Box>
        ) : (
          <Box sx={{ width: '100%' }}>
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Typography variant="h5" gutterBottom>
                Signing in with MyLogin
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please wait while we complete your authentication process.
              </Typography>
            </Box>
            
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <CircularProgress size={40} />
            </Box>
            
            <Alert severity="info" sx={{ mt: 4 }}>
              This may take a few moments. Please don't close this window.
            </Alert>
          </Box>
        )}
      </StyledPaper>
    </Container>
  );
};

export default MyLoginCallback;
